import React, { useState, useEffect } from 'react';
import './Challenge.css';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const WinterSales = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const token = localStorage.getItem('auth-token');

    const fetchSalesData = async () => {
        setError('');
        setLoading(true);
        const apiEndpoint = process.env.REACT_APP_API_WINTER_SALES;
        const headers = {
            'x-api-key': process.env.REACT_APP_API_KEY,
            'Content-Type': 'application/json',
        };

        try {
            const response = await axios.get(apiEndpoint, { headers });

            if (response.data.success) {
                const transformedSales = response.data.data.map(sale => ({
                    id: sale.id,
                    name: sale.user_name || '-',
                    mobileNumber: sale.user_phone || '-',
                    dateOfJoining: sale.joining_date || '-',
                    purchaseDate: sale.purchase_date || '-',
                    center: sale.center || '-',
                    purchaseAmount: sale.amount || '0',
                    product: sale.product || '-',
                }));

                setSales(transformedSales);
            }
        } catch (error) {
            setError(error?.message || "No data available")
            setSales([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesData();
    }, []);

    const headers = [
        { label: "Name", key: "name", isNumeric: false },
        { label: "Mobile Number", key: "mobileNumber", isNumeric: false },
        { label: "Date of Joining", key: "dateOfJoining", isNumeric: false },
        { label: "Purchase Date", key: "purchaseDate", isNumeric: false },
        { label: "Center", key: "center", isNumeric: false },
        { label: "Purchase Amount", key: "purchaseAmount", isNumeric: true },
    ];

    const [sortConfig, setSortConfig] = useState({
        key: 'purchaseDate',
        direction: 'desc',
        numericKeys: ['id', 'purchaseAmount']
    });

    const handleItemsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const filteredSales = sales.filter((sale) =>
        Object.values(sale)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortData = (data) => {
        const { key, direction } = sortConfig;
        return [...data].sort((a, b) => {
            if (key === 'purchaseAmount' || key === 'id') {
                const numA = parseFloat(a[key]) || 0;
                const numB = parseFloat(b[key]) || 0;
                return direction === 'asc' ? numA - numB : numB - numA;
            }

            if (key === 'purchaseDate' || key === 'dateOfJoining') {
                const dateA = new Date(a[key] || 0);
                const dateB = new Date(b[key] || 0);
                return direction === 'asc' ? dateA - dateB : dateB - dateA;
            }

            const valueA = String(a[key] || '').toLowerCase();
            const valueB = String(b[key] || '').toLowerCase();

            if (valueA < valueB) return direction === 'asc' ? -1 : 1;
            if (valueA > valueB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const sortedSales = sortData(filteredSales);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = sortedSales.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(sortedSales.length / rowsPerPage);

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
    };

    const generatePageNumbers = () => {
        const pages = [];
        const totalPagesToShow = 5;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderPagination = () => {
        return generatePageNumbers().map((page, index) =>
            typeof page === 'number' ? (
                <button
                    key={index}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageClick(page)}
                >
                    {page}
                </button>
            ) : (
                <span key={index} className="pagination-dots">
                    {page}
                </span>
            )
        );
    };

    const totalEntries = filteredSales.length;
    const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
    const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

    const formatDate = (dateString) => {
        if (!dateString || dateString === '-') return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatINR = (value) =>
        new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(value) || 0);


    return (
        <div className="enrollment-container">

            <div className="search-bar">
                <div className="input-container">
                    <input
                        type="text"
                        id="searchInput"
                        placeholder=""
                        className={`search-input ${searchTerm ? "has-value" : ""}`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <label htmlFor="searchInput" className="floating-label">
                        🔍 Search
                    </label>
                </div>
                <CSVLink
                    data={filteredSales}
                    headers={headers}
                    filename="WinterSales.csv"
                    className="btn btn-primary no-decoration"
                >
                    <button className="csv_button winter-sales-csv" type="button">
                        <span className="button__text">
                            Export CSV
                        </span>
                        <span className="button__icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
                            </svg>
                        </span>
                    </button>
                </CSVLink>
            </div>

            <div className="items-and-dues">
                <div className="button-group">
                    <div className="group-label">Items per page:</div>
                    <div className="button-options">
                        {[10, 50, 75, 100].map((option) => (
                            <button
                                key={option}
                                onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                                className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                            >
                                <span className="button-text">{option}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <button className="tt-action-btn winter-refresh" onClick={fetchSalesData}>
                    <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {loading && (
                <div className="premium-loader">
                    <div className="progress-track">
                        <div className="progress-bar"></div>
                    </div>
                    <div className="status-text">
                        <span>L</span>
                        <span>O</span>
                        <span>A</span>
                        <span>D</span>
                        <span>I</span>
                        <span>N</span>
                        <span>G</span>
                    </div>
                </div>
            )}
            {error && <div className="error-message">{error}</div>}
            {!loading && !error && filteredSales.length === 0 && (
                <div className="error-message">No sales data available</div>
            )}

            <div className="tt-table-container">
                <table className="tt-table winter-sales-table">
                    <thead>
                        <tr className="challenges-header-row">
                            {headers.map(({ key, label }) => (
                                <th
                                    key={key}
                                    className="challenges-column"
                                    onClick={() => handleSort(key)}
                                >
                                    <div className="th-content">
                                        <span>{label}</span>
                                        <FiChevronDown
                                            className={`sort-icon 
              ${sortConfig.key === key ? 'active' : ''} 
              ${sortConfig.key === key && sortConfig.direction === 'asc' ? 'asc' : ''} 
              ${sortConfig.key === key && sortConfig.direction === 'desc' ? 'desc' : ''}`}
                                        />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.map((sale) => (
                            <tr key={sale.id} className="challenges-row winter-sales-row">
                                <td className="challenges-cell">{sale.name}</td>
                                <td className="challenges-cell">{sale.mobileNumber}</td>
                                <td className="challenges-cell date-cell">{formatDate(sale.dateOfJoining)}</td>
                                <td className="challenges-cell date-cell purchase-date">{formatDate(sale.purchaseDate)}</td>
                                <td className="challenges-cell center-cell">{sale.center}</td>
                                <td className="challenges-cell amount-cell">{formatINR(sale.purchaseAmount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <div className="showing-entries">
                    Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
                </div>
                <button
                    className="prev-button"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                {renderPagination()}
                <button
                    className="next-button"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default WinterSales;